import { lazy } from 'react'
import { createBrowserRouter, Navigate, Outlet } from 'react-router'

import { LoggedOutRoute } from '@/components/LoggedOutRoute'
import { OrganizationMiddleware } from '@/components/OrganizationMiddleware'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { ReactRouterErrorBoundary } from '@/components/ReactRouterErrorBoundary'

const PortfolioArtifactsPage = lazy(() =>
  import(
    '@/pages/private/organizations/[id]/portfolios/[id]/artifacts/Index'
  ).then((module) => ({
    default: module.PortfolioArtifactsPage,
  }))
)

const Authentication = lazy(() =>
  import('@/pages/public/Authentication').then((module) => ({
    default: module.Authentication,
  }))
)

const SSOCallback = lazy(() =>
  import('@/pages/public/SSOCallback').then((module) => ({
    default: module.SSOCallback,
  }))
)

const OAuthContinue = lazy(() =>
  import('@/pages/public/OAuthContinue').then((module) => ({
    default: module.OAuthContinue,
  }))
)

const Invitation = lazy(() =>
  import('@/pages/public/Invitation').then((module) => ({
    default: module.Invitation,
  }))
)

const VerifyEmail = lazy(() =>
  import('@/pages/public/VerifyEmail').then((module) => ({
    default: module.VerifyEmail,
  }))
)

const ResetPassword = lazy(() =>
  import('@/pages/public/ResetPassword').then((module) => ({
    default: module.ResetPassword,
  }))
)

const NotFound = lazy(() =>
  import('@/pages/public/NotFound').then((module) => ({
    default: module.NotFound,
  }))
)

const ProfilePage = lazy(() =>
  import('@/pages/private/profile/Index').then((module) => ({
    default: module.ProfilePage,
  }))
)

const ConnectorsPage = lazy(() =>
  import('@/pages/private/connectors/Index').then((module) => ({
    default: module.ConnectorsPage,
  }))
)

const HoldingPage = lazy(() =>
  import('@/pages/private/holding/Index').then((module) => ({
    default: module.HoldingPage,
  }))
)

const PortfolioSetup = lazy(() =>
  import('@/pages/private/onboarding/portfolio/Index').then((module) => ({
    default: module.PortfolioSetup,
  }))
)

const OrganizationsPage = lazy(() =>
  import('@/pages/private/organizations/Index').then((module) => ({
    default: module.OrganizationsPage,
  }))
)

const OrganizationDetailsPage = lazy(() =>
  import('@/pages/private/organizations/[id]/Index').then((module) => ({
    default: module.OrganizationDetails,
  }))
)

const DashboardLayout = lazy(() =>
  import('@/components/layouts/Dashboard').then((module) => ({
    default: module.Dashboard,
  }))
)

const PortfolioHomePage = lazy(() =>
  import('@/pages/private/organizations/[id]/portfolios/[id]/home/Index').then(
    (module) => ({
      default: module.PortfolioHomePage,
    })
  )
)

const PortfolioRoadmapPage = lazy(() =>
  import(
    '@/pages/private/organizations/[id]/portfolios/[id]/roadmap/Index'
  ).then((module) => ({
    default: module.PortfolioRoadmapPage,
  }))
)

const AddProductPage = lazy(() =>
  import(
    '@/pages/private/organizations/[id]/portfolios/[id]/products/new/Index'
  ).then((module) => ({
    default: module.AddProductPage,
  }))
)

const ProductDetailsPage = lazy(() =>
  import(
    '@/pages/private/organizations/[id]/portfolios/[id]/products/[id]/Index'
  ).then((module) => ({
    default: module.ProductDetailsPage,
  }))
)

const ProductHomePage = lazy(() =>
  import(
    '@/pages/private/organizations/[id]/portfolios/[id]/products/[id]/home/Index'
  ).then((module) => ({
    default: module.ProductHomePage,
  }))
)

const ProductIdeasPage = lazy(() =>
  import(
    '@/pages/private/organizations/[id]/portfolios/[id]/products/[id]/ideas/Index'
  ).then((module) => ({
    default: module.ProductIdeasPage,
  }))
)

const ProductIdeaDetailsPage = lazy(() =>
  import(
    '@/pages/private/organizations/[id]/portfolios/[id]/products/[id]/ideas/[id]/Index'
  ).then((module) => ({
    default: module.ProductIdeaDetailsPage,
  }))
)

const ProductHypothesesPage = lazy(() =>
  import(
    '@/pages/private/organizations/[id]/portfolios/[id]/products/[id]/hypotheses/Index'
  ).then((module) => ({
    default: module.ProductHypothesesPage,
  }))
)

const ProductHypothesisDetailsPage = lazy(() =>
  import(
    '@/pages/private/organizations/[id]/portfolios/[id]/products/[id]/hypotheses/[id]/Index'
  ).then((module) => ({
    default: module.ProductHypothesisDetailsPage,
  }))
)

const ProductExperimentsPage = lazy(() =>
  import(
    '@/pages/private/organizations/[id]/portfolios/[id]/products/[id]/experiments/Index'
  ).then((module) => ({
    default: module.ProductExperimentsPage,
  }))
)

const ProductExperimentDetailsPage = lazy(() =>
  import(
    '@/pages/private/organizations/[id]/portfolios/[id]/products/[id]/experiments/[id]/Index'
  ).then((module) => ({
    default: module.ProductExperimentDetailsPage,
  }))
)

const ProductInsightsPage = lazy(() =>
  import(
    '@/pages/private/organizations/[id]/portfolios/[id]/products/[id]/insights/Index'
  ).then((module) => ({
    default: module.ProductInsightsPage,
  }))
)

const ProductInsightPage = lazy(() =>
  import(
    '@/pages/private/organizations/[id]/portfolios/[id]/products/[id]/insights/[id]/Index'
  ).then((module) => ({
    default: module.ProductInsightPage,
  }))
)

const ProductArtifactsPage = lazy(() =>
  import(
    '@/pages/private/organizations/[id]/portfolios/[id]/products/[id]/artifacts/Index'
  ).then((module) => ({
    default: module.ProductArtifactsPage,
  }))
)

const ArtifactDetailPage = lazy(() =>
  import(
    '@/pages/private/organizations/[id]/portfolios/[id]/products/[id]/artifacts/[id]/Index'
  ).then((module) => ({
    default: module.ArtifactDetailPage,
  }))
)

const ProductSettingsPage = lazy(() =>
  import(
    '@/pages/private/organizations/[id]/portfolios/[id]/products/[id]/settings/Index'
  ).then((module) => ({
    default: module.ProductSettingsPage,
  }))
)

const PortfolioSettings = lazy(() =>
  import(
    '@/pages/private/organizations/[id]/portfolios/[id]/settings/Index'
  ).then((module) => ({ default: module.PortfolioSettings }))
)

const ProductContextRoute = lazy(() =>
  import(
    '@/pages/private/organizations/[id]/portfolios/[id]/products/[id]/settings/context/Index'
  ).then((module) => ({
    default: module.ProductContextRoute,
  }))
)

const ProductIntegrationsRoute = lazy(() =>
  import(
    '@/pages/private/organizations/[id]/portfolios/[id]/products/[id]/settings/integrations/Index'
  ).then((module) => ({
    default: module.ProductIntegrationsRoute,
  }))
)
const ProductTeamRoute = lazy(() =>
  import(
    '@/pages/private/organizations/[id]/portfolios/[id]/products/[id]/settings/team/Index'
  ).then((module) => ({
    default: module.ProductTeamRoute,
  }))
)

export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <LoggedOutRoute>
        <Authentication />
      </LoggedOutRoute>
    ),
    ErrorBoundary: ReactRouterErrorBoundary,
  },
  {
    path: '/auth/sso-callback',
    element: (
      <LoggedOutRoute>
        <SSOCallback />
      </LoggedOutRoute>
    ),
    ErrorBoundary: ReactRouterErrorBoundary,
  },
  {
    path: '/auth/continue',
    element: (
      <LoggedOutRoute>
        <OAuthContinue />
      </LoggedOutRoute>
    ),
    ErrorBoundary: ReactRouterErrorBoundary,
  },
  {
    path: '/invitation',
    element: (
      <LoggedOutRoute>
        <Invitation />
      </LoggedOutRoute>
    ),
    ErrorBoundary: ReactRouterErrorBoundary,
  },
  {
    path: '/verify-email',
    element: (
      <LoggedOutRoute>
        <VerifyEmail />
      </LoggedOutRoute>
    ),
    ErrorBoundary: ReactRouterErrorBoundary,
  },
  {
    path: '/reset-password',
    element: (
      <LoggedOutRoute>
        <ResetPassword />
      </LoggedOutRoute>
    ),
    ErrorBoundary: ReactRouterErrorBoundary,
  },
  {
    path: '/holding',
    element: (
      <ProtectedRoute>
        <HoldingPage />
      </ProtectedRoute>
    ),
    ErrorBoundary: ReactRouterErrorBoundary,
  },
  {
    path: '/onboarding/portfolio',
    element: (
      <ProtectedRoute>
        <PortfolioSetup />
      </ProtectedRoute>
    ),
    errorElement: <NotFound />,
  },
  {
    path: '/organizations',
    element: (
      <ProtectedRoute>
        <OrganizationsPage />
      </ProtectedRoute>
    ),
    ErrorBoundary: ReactRouterErrorBoundary,
  },
  {
    path: '/organizations/:organizationId',
    element: (
      <OrganizationMiddleware>
        <ProtectedRoute>
          <Outlet />
        </ProtectedRoute>
      </OrganizationMiddleware>
    ),
    ErrorBoundary: ReactRouterErrorBoundary,
    children: [
      {
        index: true,
        element: <OrganizationDetailsPage />,
      },
      {
        path: 'portfolios/:portfolioId/products/new',
        element: <AddProductPage />,
      },
      {
        path: 'portfolios/:portfolioId',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="home" replace />,
          },
          { path: 'home', element: <PortfolioHomePage /> },
          { path: 'roadmap', element: <PortfolioRoadmapPage /> },
          {
            path: 'artifacts',
            element: <Outlet />,
            children: [{ index: true, element: <PortfolioArtifactsPage /> }],
          },
          { path: 'settings', element: <PortfolioSettings /> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'connectors', element: <ConnectorsPage /> },
          {
            path: 'products/:productId',
            element: <Outlet />,
            children: [
              {
                index: true,
                element: <ProductDetailsPage />,
              },
              { path: 'home', element: <ProductHomePage /> },
              {
                path: 'ideas',
                element: <Outlet />,
                children: [
                  {
                    index: true,
                    element: <ProductIdeasPage />,
                  },
                  {
                    path: 'funded',
                    element: <ProductIdeasPage status="funded" />,
                  },
                  {
                    path: 'shipped',
                    element: <ProductIdeasPage status="shipped" />,
                  },
                  {
                    path: 'parked',
                    element: <ProductIdeasPage status="parked" />,
                  },
                  {
                    path: 'killed',
                    element: <ProductIdeasPage status="killed" />,
                  },
                  {
                    path: ':ideaId',
                    element: <ProductIdeaDetailsPage />,
                  },
                ],
              },
              {
                path: 'hypotheses',
                element: <Outlet />,
                children: [
                  { index: true, element: <ProductHypothesesPage /> },
                  {
                    path: ':hypothesisId',
                    element: <ProductHypothesisDetailsPage />,
                  },
                ],
              },
              {
                path: 'experiments',
                element: <Outlet />,
                children: [
                  { index: true, element: <ProductExperimentsPage /> },
                  {
                    path: ':experimentId',
                    element: <ProductExperimentDetailsPage />,
                  },
                ],
              },
              {
                path: 'insights',
                element: <Outlet />,
                children: [
                  { index: true, element: <ProductInsightsPage /> },
                  { path: ':insightId', element: <ProductInsightPage /> },
                ],
              },
              {
                path: 'artifacts',
                element: <Outlet />,
                children: [
                  { index: true, element: <ProductArtifactsPage /> },
                  { path: ':artifactId', element: <ArtifactDetailPage /> },
                ],
              },
              {
                path: 'settings',
                element: <ProductSettingsPage />,
                children: [
                  { index: true, element: <Navigate to="context" replace /> },
                  { path: 'context', element: <ProductContextRoute /> },
                  { path: 'team', element: <ProductTeamRoute /> },
                  {
                    path: 'integrations',
                    element: <ProductIntegrationsRoute />,
                  },
                ],
              },
              { path: 'profile', element: <ProfilePage /> },
              { path: 'connectors', element: <ConnectorsPage /> },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <NotFound />,
    ErrorBoundary: ReactRouterErrorBoundary,
  },
])
